import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { MapPinIcon, PhoneIcon, MailIcon } from 'lucide-react';
const fadeInUp = {
  initial: {
    opacity: 0,
    y: 30
  },
  whileInView: {
    opacity: 1,
    y: 0
  },
  viewport: {
    once: true,
    margin: '-100px'
  },
  transition: {
    duration: 0.7,
    ease: 'easeOut'
  }
};
function Section({
  title,
  children
}: {
  title: string;
  children: React.ReactNode;
}) {
  return <motion.div {...fadeInUp} className="mb-10" data-id="element-483">
      <h2 className="font-serif text-2xl md:text-3xl font-bold text-brand-green mb-4" data-id="element-484">
        {title}
      </h2>
      <div className="space-y-4 text-gray-600 leading-relaxed" data-id="element-485">{children}</div>
    </motion.div>;
}
function BulletList({
  items
}: {
  items: string[];
}) {
  return <ul className="space-y-2" data-id="element-486">
      {items.map(item => <li key={item} className="flex items-start" data-id="element-487">
          <span className="mt-2 mr-3 h-1.5 w-1.5 rounded-full bg-brand-gold shrink-0" data-id="element-488"></span>
          <span data-id="element-489">{item}</span>
        </li>)}
    </ul>;
}
export function Terms() {
  return <div className="w-full pt-24 bg-brand-offwhite" data-id="element-490">
      {/* Page Header */}
      <section className="relative py-20 bg-brand-charcoal text-white overflow-hidden" data-id="element-491">
        <div className="absolute inset-0 z-0 opacity-20" data-id="element-492">
          <img src="https://images.unsplash.com/photo-1450101499163-c8848c66ca85?auto=format&fit=crop&q=80&w=2000" alt="" aria-hidden="true" className="w-full h-full object-cover" data-id="element-493" />
        </div>
        <div className="relative z-10 max-w-container mx-auto px-6 text-center" data-id="element-494">
          <motion.h1 initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.8
        }} className="font-serif text-5xl md:text-6xl font-bold mb-4" data-id="element-495">
            Terms of Service
          </motion.h1>
          <motion.p initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} transition={{
          duration: 0.8,
          delay: 0.2
        }} className="text-sm uppercase tracking-[0.2em] text-brand-gold" data-id="element-496">
            Last Updated: June 2026
          </motion.p>
        </div>
      </section>

      {/* Content */}
      <section className="py-20" data-id="element-497">
        <div className="max-w-3xl mx-auto px-6" data-id="element-498">
          <motion.p {...fadeInUp} className="text-lg text-gray-600 leading-relaxed mb-12" data-id="element-499">
            Welcome to the McErick Attorneys website. By accessing or using this
            website, you agree to these Terms of Service. If you do not agree
            with these terms, please discontinue use of the website.
          </motion.p>

          <Section title="Website Purpose" data-id="element-500">
            <p data-id="element-501">
              This website provides general information about McErick Attorneys,
              our legal services, and how to contact our firm.
            </p>
            <p data-id="element-502">
              The information provided is for general informational purposes
              only and should not be considered legal advice.
            </p>
          </Section>

          <Section title="No Attorney-Client Relationship" data-id="element-503">
            <p data-id="element-504">
              Your use of this website, including submitting enquiries or
              contacting us through online forms or email, does not create an
              attorney-client relationship.
            </p>
            <p data-id="element-505">
              An attorney-client relationship is only established after formal
              acceptance of your matter by McErick Attorneys and any required
              engagement documentation has been completed.
            </p>
          </Section>

          <Section title="Legal Advice" data-id="element-506">
            <p data-id="element-507">Legal matters differ depending on individual circumstances.</p>
            <p data-id="element-508">
              Information published on this website should not be relied upon as
              a substitute for professional legal advice.
            </p>
            <p data-id="element-509">
              We encourage you to contact our office to obtain advice specific
              to your situation.
            </p>
          </Section>

          <Section title="Intellectual Property" data-id="element-510">
            <p data-id="element-511">All website content, including:</p>
            <BulletList items={['Text', 'Graphics', 'Images', 'Logos', 'Icons', 'Design', 'Layout', 'Documents', 'Branding']} data-id="element-512" />
            <p data-id="element-513">
              is the property of McErick Attorneys or used under appropriate
              permission and is protected by applicable intellectual property
              laws.
            </p>
            <p data-id="element-514">
              No content may be copied, reproduced, distributed, or modified
              without prior written permission.
            </p>
          </Section>

          <Section title="User Responsibilities" data-id="element-515">
            <p data-id="element-516">Users agree not to:</p>
            <BulletList items={['Use the website for unlawful purposes', 'Submit false or misleading information', 'Attempt unauthorized access to website systems', 'Interfere with website functionality', 'Upload malicious software or harmful content']} data-id="element-517" />
          </Section>

          <Section title="Limitation of Liability" data-id="element-518">
            <p data-id="element-519">
              While we strive to ensure that information on this website is
              accurate and current, McErick Attorneys makes no warranties
              regarding the completeness, accuracy, or reliability of website
              content.
            </p>
            <p data-id="element-520">
              To the fullest extent permitted by law, we shall not be liable for
              any direct, indirect, incidental, or consequential damages arising
              from use of this website.
            </p>
          </Section>

          <Section title="Third-Party Websites" data-id="element-521">
            <p data-id="element-522">Links to external websites are provided for convenience only.</p>
            <p data-id="element-523">
              McErick Attorneys does not endorse or accept responsibility for
              the content, services, or privacy practices of third-party
              websites.
            </p>
          </Section>

          <Section title="Privacy" data-id="element-524">
            <p data-id="element-525">
              Your use of this website is also governed by our{' '}
              <Link to="/privacy" className="text-brand-green font-medium hover:text-brand-gold transition-colors" data-id="element-526">
                Privacy Policy
              </Link>
              .
            </p>
          </Section>

          <Section title="Changes to These Terms" data-id="element-527">
            <p data-id="element-528">
              McErick Attorneys reserves the right to amend these Terms of
              Service at any time without prior notice.
            </p>
            <p data-id="element-529">
              Continued use of the website following changes constitutes
              acceptance of the revised terms.
            </p>
          </Section>

          <Section title="Governing Law" data-id="element-530">
            <p data-id="element-531">
              These Terms of Service shall be governed by and interpreted in
              accordance with the laws of the Republic of Botswana.
            </p>
            <p data-id="element-532">
              Any disputes arising from the use of this website shall be subject
              to the jurisdiction of the courts of Botswana.
            </p>
          </Section>

          <Section title="Contact Information" data-id="element-533">
            <p data-id="element-534">
              For questions regarding these Terms of Service, please contact:
            </p>
            <div className="mt-6 rounded-2xl bg-white border border-gray-100 shadow-soft p-8 space-y-4" data-id="element-535">
              <p className="font-serif text-xl font-bold text-brand-charcoal" data-id="element-536">
                McErick Attorneys
              </p>
              <div className="flex items-start text-sm" data-id="element-537">
                <MapPinIcon className="w-5 h-5 mr-3 text-brand-gold shrink-0 mt-0.5" data-id="element-538" />
                <span data-id="element-539">
                  Plot 70667, 7th Floor, The Tower, Farscape Precinct,
                  Fairgrounds Office Park, Gaborone, Botswana
                </span>
              </div>
              <a href="tel:+2673165472" className="flex items-center text-sm hover:text-brand-green transition-colors" data-id="element-540">
                <PhoneIcon className="w-5 h-5 mr-3 text-brand-gold shrink-0" data-id="element-541" />
                Telephone: (+267) 316 5472
              </a>
              <a href="tel:+26772553210" className="flex items-center text-sm hover:text-brand-green transition-colors" data-id="element-542">
                <PhoneIcon className="w-5 h-5 mr-3 text-brand-gold shrink-0" data-id="element-543" />
                Mobile: (+267) 7255 3210
              </a>
              <a href="mailto:charles@mcerickattorneys.co.bw" className="flex items-center text-sm hover:text-brand-green transition-colors" data-id="element-544">
                <MailIcon className="w-5 h-5 mr-3 text-brand-gold shrink-0" data-id="element-545" />
                charles@mcerickattorneys.co.bw
              </a>
            </div>
          </Section>
        </div>
      </section>
    </div>;
}